/**
 * CadenceSequencePreview.jsx — pick a tracked list, see its full computed
 * Follow-up sequence in one table: past stages show the real sent date,
 * future stages show the computed due date (see buildListTimeline in
 * backend/cadence.js) — so you can sanity-check a sequence you just set up
 * in TrackedListsPanel without hunting for it in the week-by-week Calendar.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchCadence } from '../api/backend.js';

const STATUS_LABEL = {
  sent: 'Sent',
  overdue: 'Overdue',
  due_soon: 'Due soon',
  upcoming: 'Upcoming',
  not_started: 'Not started',
  not_tracked: 'Not tracked',
  unknown: '—',
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function CadenceSequencePreview({ getToken }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timelines, setTimelines] = useState([]);
  const [selectedList, setSelectedList] = useState('');

  const load = useCallback(async () => {
    if (!getToken) return;
    setLoading(true);
    setError('');
    try {
      const cadence = await fetchCadence(getToken);
      const sorted = [...(cadence.timelines ?? [])].sort((a, b) => a.listName.localeCompare(b.listName));
      setTimelines(sorted);
      setSelectedList((prev) => (prev && sorted.some((t) => t.listName === prev) ? prev : sorted[0]?.listName ?? ''));
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { if (expanded) load(); }, [expanded, load]);

  const selected = useMemo(() => timelines.find((t) => t.listName === selectedList) ?? null, [timelines, selectedList]);

  return (
    <div className="bd-cadence-backfill" style={{ marginBottom: 16 }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="btn btn--ghost btn--sm"
        style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}
      >
        <span>📈 Sequence Preview (past + upcoming Follow-ups for one list)</span>
        <span>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div style={{ marginTop: 8, padding: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)' }}>
          {loading && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>}
          {error && <div style={{ fontSize: 12.5, color: '#dc2626', marginBottom: 8 }}>{error}</div>}

          {!loading && !timelines.length && (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No uploaded list names yet.</div>
          )}

          {!loading && timelines.length > 0 && (
            <>
              <select
                value={selectedList}
                onChange={(e) => setSelectedList(e.target.value)}
                style={{ display: 'block', width: '100%', marginBottom: 10, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
              >
                {timelines.map((t) => (
                  <option key={t.listName} value={t.listName}>
                    {t.listName}{t.predicted ? ' (upcoming, not sent yet)' : ''}{!t.isTrackedCadence ? ' — not tracked' : ''}
                  </option>
                ))}
              </select>

              {selected && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 11 }}>
                      <th style={{ padding: '4px 6px' }}>Stage</th>
                      <th style={{ padding: '4px 6px' }}>Date</th>
                      <th style={{ padding: '4px 6px' }}>Status</th>
                      <th style={{ padding: '4px 6px' }}>Open rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.stages.map((s) => (
                      <tr key={s.stage} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 6px', fontWeight: 700 }}>{s.label}</td>
                        <td style={{ padding: '5px 6px' }}>
                          {s.sentAt ? `Sent ${fmtDate(s.sentAt)}` : (s.dueDate ? `Due ${fmtDate(s.dueDate)}` : '—')}
                        </td>
                        <td style={{ padding: '5px 6px' }}>{STATUS_LABEL[s.status] ?? s.status}</td>
                        <td style={{ padding: '5px 6px' }}>{s.openRate != null ? `${Math.round(s.openRate * 100)}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {selected?.extraSends?.length > 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8 }}>
                  + {selected.extraSends.length} extra send{selected.extraSends.length > 1 ? 's' : ''} beyond the tracked sequence (not shown above).
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
